import { type Action, Input } from './input';

/**
 * Radians per accumulated look pixel in the spring arm (`ROSALIE_CAMERA.mouseSensitivity`).
 *
 * Restated rather than imported so the input layer does not pull the camera and
 * Three into itself; `touch-layout.spec.ts` fails if the two ever disagree.
 */
export const CAMERA_RADIANS_PER_PIXEL = 0.0025;

/**
 * How far a drag across one third of the screen's SHORTER side turns the camera.
 *
 * A quarter turn. The constant this replaced promised the same in its comment
 * and delivered a half turn — `(3π / 0.0025) / 3 × 0.0025 = π` — and it scaled
 * each axis by its own viewport dimension, so on a portrait phone a vertical
 * drag was 2.2× slower than the same drag sideways. One scale, from the short
 * side, turns both axes alike and means the same thumb travel on every device.
 */
export const TOUCH_LOOK_RADIANS_PER_THIRD = Math.PI / 2;

/** Raw `Input` look pixels per CSS pixel of finger travel, at this viewport. */
export function touchLookScale(width: number, height: number): number {
  const shortSide = Math.max(1, Math.min(width, height));
  return TOUCH_LOOK_RADIANS_PER_THIRD / (shortSide / 3) / CAMERA_RADIANS_PER_PIXEL;
}

/**
 * The sizes of the touch layer at one viewport, in CSS pixels.
 *
 * Everything is derived from the SHORT side, because the short side is what
 * ran out: six 72 px buttons in a column are 552 px, and an 844 × 390 phone in
 * landscape has 390 — the first two verbs were drawn at y −186 and −90 and
 * NOTES sat on top of the Pause pill. Tablets still get the 72 px buttons and
 * the 96 px stick they had; phones get 52 px, above the 44–48 px a finger needs.
 *
 * `stickRadius` is ONE number for both the drawing and the input: the rim of the
 * drawn stick is full speed. A CSS-only resize would have left input normalising
 * against 96 px under a 70 px ring, so the visible edge would have been 70 %.
 */
export interface TouchLayout {
  button: number;
  gap: number;
  margin: number;
  stickRadius: number;
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export function touchLayout(width: number, height: number): TouchLayout {
  const shortSide = Math.max(1, Math.min(width, height));
  const button = Math.round(clamp(shortSide * 0.12, 52, 72));
  return {
    button,
    gap: Math.round(button * 0.25),
    margin: Math.round(clamp(shortSide * 0.04, 12, 24)),
    stickRadius: Math.round(clamp(shortSide * 0.18, 64, 96)),
  };
}

/**
 * The small resting movement a thumb makes before it intends to walk, in CSS px.
 *
 * Fixed, NOT a fraction of the stick: it is how much a thumb wobbles, which
 * does not shrink with the phone. Scaled with a 70 px phone stick it fell to
 * 7 px and an 8 px wobble started her walking (`touch.spec.ts`, dead zone).
 */
export const TOUCH_STICK_DEAD_ZONE = 9.6;

type TouchRole =
  | { kind: 'move' }
  | { kind: 'look'; lastX: number; lastY: number };

interface TouchButton {
  action: Extract<Action, 'sneak' | 'roll' | 'pause' | 'interact' | 'observe' | 'notes' | 'callHolly'>;
  element: HTMLButtonElement;
  /** The pointer that owns this button's held action, or null when not held. */
  pointerId: number | null;
}

type TouchAction = TouchButton['action'];
/**
 * What the verb buttons are offering right now, and which control holds a sheet up.
 *
 * The map is not a verb: it has its own NOTES button, always shown (D-237). It
 * used to be a third state of WATCH, which made it unreachable whenever
 * something watchable was in reach — on a phone, the only device this game is
 * reviewed on (D-125), that was all of beat 10.
 *
 * `sheet` names the control holding a map up — NOTES for hers, WATCH for
 * Kestner's wall in beat 11 — so that one stays visible while the other verbs
 * step out of the sheet's way.
 */
export interface VerbAvailability {
  interact: 'nudge' | 'root' | null;
  observe: 'observe' | null;
  call: 'call' | 'send' | null;
  sheet?: 'notes' | 'observe' | null;
}

const VERB_WORDS: Readonly<Record<string, { glyph: string; label: string }>> = {
  nudge: { glyph: 'NUDGE', label: 'Nudge' },
  root: { glyph: 'ROOT', label: 'Root' },
  observe: { glyph: 'WATCH', label: 'Watch' },
  call: { glyph: 'CALL', label: 'Call Holly' },
  send: { glyph: 'SEND', label: 'Send Holly' },
};

/**
 * Touch overlay and Pointer Events adapter for `Input`.
 *
 * It starts as an event listener, rather than a persistent HUD, because a
 * touch-capable hybrid still deserves an entirely ordinary mouse-first game
 * until a finger actually chooses the touch affordance.
 */
const ROTATE_HINT_KEY = 'rosalie:rotate-hint-seen';

// Storage can be absent or throw (private windows, blocked site data); the
// page's own flag then keeps it to once per page, which is the harmless side
// to fail on. Without the flag it came back on every rotation into portrait
// (found on review).
let rotateHintShownThisPage = false;

function rotateHintSeen(): boolean {
  if (rotateHintShownThisPage) return true;
  try {
    return window.sessionStorage.getItem(ROTATE_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function markRotateHintSeen(): void {
  rotateHintShownThisPage = true;
  try {
    window.sessionStorage.setItem(ROTATE_HINT_KEY, '1');
  } catch {
    // See `rotateHintSeen`.
  }
}

export class TouchControls {
  #target: HTMLElement | null = null;
  #root: HTMLDivElement | null = null;
  #stick: HTMLDivElement | null = null;
  #rotateHint: HTMLDivElement | null = null;
  #style: HTMLStyleElement | null = null;
  #buttons: TouchButton[] = [];
  #touches = new Map<number, TouchRole>();
  #movePointerId: number | null = null;
  #enabled = false;
  #suppressClickUntil = 0;
  #previousTouchAction = '';
  #detachListeners: (() => void) | null = null;
  #verbAvailability: Required<VerbAvailability> = { interact: null, observe: null, call: null, sheet: null };
  #layout: TouchLayout = touchLayout(window.innerWidth, window.innerHeight);
  #landscape = window.innerWidth > window.innerHeight;

  constructor(readonly input: Input) {}

  attach(target: HTMLElement): void {
    this.detach();
    this.#target = target;

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== 'touch') return;
      if (!this.#enabled) this.#enable();
      this.#beginSurfaceTouch(event);
    };
    const onPointerMove = (event: PointerEvent): void => this.#moveSurfaceTouch(event);
    const onPointerUp = (event: PointerEvent): void => this.#endSurfaceTouch(event);
    const onPointerCancel = (event: PointerEvent): void => this.#endSurfaceTouch(event);
    const onClickCapture = (event: MouseEvent): void => {
      // Exempt from the simulation guard: touch is input, and this wall-clock
      // deadline only filters the synthetic click generated by a finger tap.
      if (performance.now() > this.#suppressClickUntil) return;
      this.#suppressClickUntil = 0;
      // A touch also synthesizes a click. Letting that reach main's canvas
      // listener would request mouse pointer lock while the player is dragging.
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const onBlur = (): void => this.#clearTouches();
    // A phone that locks or switches apps need not blur the window, nor cancel
    // the pointers it was holding; main pauses on this same event, and without
    // it a held stick or verb was still held on the way back in.
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') this.#clearTouches();
    };
    const onResize = (): void => this.#resize();

    target.addEventListener('pointerdown', onPointerDown);
    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerup', onPointerUp);
    target.addEventListener('pointercancel', onPointerCancel);
    target.addEventListener('click', onClickCapture, true);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);
    this.#detachListeners = (): void => {
      target.removeEventListener('pointerdown', onPointerDown);
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerUp);
      target.removeEventListener('pointercancel', onPointerCancel);
      target.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };

    if (window.matchMedia('(pointer: coarse)').matches) this.#enable();
  }

  detach(): void {
    this.#detachListeners?.();
    this.#detachListeners = null;
    this.input.setTouchAxis(0, 0, false);
    for (const button of this.#buttons) this.input.setTouchAction(button.action, false);
    this.#touches.clear();
    this.#movePointerId = null;
    this.#suppressClickUntil = 0;
    this.#root?.remove();
    this.#style?.remove();
    if (this.#target) this.#target.style.touchAction = this.#previousTouchAction;
    this.#target = null;
    this.#root = null;
    this.#stick = null;
    this.#rotateHint = null;
    this.#style = null;
    this.#buttons = [];
    this.#enabled = false;
  }

  /** Whether this device has activated the touch presentation and input path. */
  get enabled(): boolean {
    return this.#enabled;
  }

  /**
   * True while a finger is down on the look half, moving or not.
   *
   * The camera reads this to hold off its idle recentre: a resting thumb is a
   * player holding the view where they put it, and treating "no delta this
   * frame" as idle swung the camera back under a finger that was still down.
   */
  get lookHeld(): boolean {
    for (const role of this.#touches.values()) if (role.kind === 'look') return true;
    return false;
  }

  /** The current sizes, for the harness. */
  get layout(): TouchLayout {
    return { ...this.#layout };
  }

  setVerbAvailability(available: VerbAvailability): void {
    const sheet = available.sheet ?? null;
    const current = this.#verbAvailability;
    if (available.interact === current.interact && available.observe === current.observe
      && available.call === current.call && sheet === current.sheet) return;
    this.#verbAvailability = { interact: available.interact, observe: available.observe, call: available.call, sheet };
    this.#applyVerbAvailability();
  }

  #enable(): void {
    if (this.#enabled || !this.#target) return;
    this.#enabled = true;
    this.#previousTouchAction = this.#target.style.touchAction;
    this.#target.style.touchAction = 'none';

    const style = document.createElement('style');
    style.dataset.rosalieTouchControls = '';
    style.textContent = `
      /*
       * Nothing in the touch layer is text, even though some of it is spelled
       * with letters.
       *
       * The verb buttons say NUDGE and WATCH and the gait buttons CREEP and
       * ROLL, and a tap that moves a few pixels — which is every tap on a
       * tablet held in two hands — is a text selection gesture as far as the
       * browser is concerned. Alex's report: "the letters in the buttons on the
       * right sometimes get selected when tapping". What he was seeing is the
       * glyph going blue with two drag handles on it, over a game.
       *
       * touch-action: none on the buttons does not cover this: it governs
       * scrolling and zooming, not selection, and not the long-press callout
       * that offers to look the letter up. Those need user-select and
       * -webkit-touch-callout, and they are set on the whole control root
       * rather than per button so the stick and the pause pill cannot acquire
       * the same bug later.
       */
      .rosalie-touch-controls, .rosalie-touch-controls * {
        -webkit-user-select: none; user-select: none;
        -webkit-touch-callout: none;
      }
      .rosalie-touch-controls { position: fixed; inset: 0; z-index: 10; pointer-events: none; }
      .rosalie-touch-stick { position: fixed; width: calc(var(--stick-r) * 2); height: calc(var(--stick-r) * 2); opacity: 0; pointer-events: none; transform: translate(-50%, -50%); transition: opacity 0.4s ease; }
      .rosalie-touch-stick.is-active { opacity: 0.5; }
      .rosalie-touch-stick-base, .rosalie-touch-stick-knob, .rosalie-touch-action { box-sizing: border-box; border: 2px solid rgba(230, 226, 221, 1); border-radius: 50%; color: rgba(230, 226, 221, 1); }
      .rosalie-touch-stick-base { position: absolute; inset: 0; }
      .rosalie-touch-stick-knob { position: absolute; left: calc(50% + var(--knob-x, 0px)); top: calc(50% + var(--knob-y, 0px)); width: calc(var(--stick-r) * 0.5); height: calc(var(--stick-r) * 0.5); transform: translate(-50%, -50%); }
      /*
       * A thumb cluster in fixed slots, bottom-right — never a column. Slots do
       * not move when a verb comes and goes, so a thumb learns where WATCH is.
       * Only the right half of the screen: a touch that starts on the left half
       * is always the stick. On a narrow phone the stick's DRAWN rim can reach
       * past the midline under the cluster (70 px from an origin just left of
       * it, at 390 px wide); that is paint only — the canvas holds that
       * pointer's capture, so a drag across a button cannot press it.
       */
      .rosalie-touch-actions {
        position: fixed;
        right: calc(env(safe-area-inset-right) + var(--margin));
        bottom: calc(env(safe-area-inset-bottom) + var(--margin));
        display: grid; gap: var(--gap);
        grid-template-columns: var(--button) var(--button);
        grid-auto-rows: var(--button);
        grid-template-areas: "notes callHolly" "observe interact" "sneak roll";
        pointer-events: none;
      }
      .rosalie-touch-action { appearance: none; width: var(--button); height: var(--button); padding: 0; display: grid; place-items: center; background: transparent; box-shadow: none; font: 500 clamp(10px, calc(var(--button) * 0.17), 12px)/1 system-ui, sans-serif; letter-spacing: 0.06em; opacity: 0; pointer-events: auto; touch-action: none; transition: opacity 0.12s ease; }
      .rosalie-touch-controls.is-used .rosalie-touch-action { opacity: 0.28; }
      .rosalie-touch-controls.is-used .rosalie-touch-action.is-available { opacity: 0.62; }
      .rosalie-touch-action.is-active { opacity: 0.5 !important; }
      .rosalie-touch-action.is-available.is-active { opacity: 0.72 !important; }
      /*
       * An offered-nothing slot is invisible but still a hit target that
       * swallows the touch. A thumb that lands where it expects WATCH must not
       * start a camera drag instead — and pointer-events: none (or visibility:
       * hidden) would hand exactly that touch to the look surface underneath.
       */
      .rosalie-touch-controls .rosalie-touch-action.is-unoffered { opacity: 0 !important; }
      .rosalie-touch-pause { position: fixed; top: calc(env(safe-area-inset-top) + var(--margin)); right: calc(env(safe-area-inset-right) + var(--margin)); width: auto; min-width: 5.25rem; height: 48px; padding: 0 14px; border-radius: 999px; font-size: 14px; letter-spacing: normal; opacity: 0.72; }
      .rosalie-touch-controls .rosalie-touch-pause { opacity: 0.72; }
      .rosalie-touch-controls.is-used .rosalie-touch-pause { opacity: 0.72; }
      .rosalie-touch-pause.is-active { opacity: 0.9 !important; }
      /*
       * "Turn your phone" (D-247). Top-left, clear of the Pause pill, never a
       * hit target: it must not swallow the first look drag. It fades in,
       * holds and fades out on its own animation, so there is no timer.
       */
      .rosalie-touch-rotate {
        position: fixed;
        top: calc(env(safe-area-inset-top) + var(--margin));
        left: calc(env(safe-area-inset-left) + var(--margin));
        right: calc(env(safe-area-inset-right) + var(--margin) * 2 + 5.25rem);
        min-height: 48px; display: flex; align-items: center;
        font: 500 13px/1.3 system-ui, sans-serif; color: rgba(230, 226, 221, 1);
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.85);
        opacity: 0; pointer-events: none;
      }
      .rosalie-touch-rotate.is-shown { animation: rosalie-touch-rotate 7s ease forwards; }
      @keyframes rosalie-touch-rotate { 0% { opacity: 0; } 8% { opacity: 0.85; } 80% { opacity: 0.85; } 100% { opacity: 0; } }
    `;
    document.head.append(style);
    this.#style = style;

    const root = document.createElement('div');
    root.className = 'rosalie-touch-controls';
    root.dataset.rosalieTouchControls = '';
    const stick = document.createElement('div');
    stick.className = 'rosalie-touch-stick';
    stick.innerHTML = '<div class="rosalie-touch-stick-base"></div><div class="rosalie-touch-stick-knob"></div>';
    root.append(stick);

    const actions = document.createElement('div');
    actions.className = 'rosalie-touch-actions';
    actions.append(this.#button('interact', 'Nudge', 'NUDGE'));
    actions.append(this.#button('observe', 'Watch', 'WATCH'));
    // NOTES is a HOLD — the sheet is up while it is down — so it lives in the
    // cluster, in thumb reach, rather than up beside Pause.
    actions.append(this.#button('notes', 'Notes', 'NOTES'));
    actions.append(this.#button('callHolly', 'Call Holly', 'CALL'));
    actions.append(this.#button('sneak', 'Creep', 'CREEP'));
    actions.append(this.#button('roll', 'Roll', 'ROLL'));
    root.append(actions);
    // Unlike movement controls, pause is an exit affordance rather than a
    // gameplay hint. Hiding it until the first drag recreated the tablet bug:
    // the control intended to get a player out could not be found before they
    // had already guessed how to start moving.
    root.append(this.#button('pause', 'Pause', 'Pause'));
    const rotateHint = document.createElement('div');
    rotateHint.className = 'rosalie-touch-rotate';
    rotateHint.textContent = 'Best played with the phone sideways';
    root.append(rotateHint);
    document.body.append(root);
    this.#root = root;
    this.#stick = stick;
    this.#rotateHint = rotateHint;
    this.#applyLayout();
    this.#applyVerbAvailability();
    this.#updateRotateHint();
  }

  /**
   * Suggest landscape, once per session, while the frame is portrait (D-247).
   *
   * Portrait plays — the lens widens (D-245) and the openings have portrait
   * compositions (D-246) — but every shot was composed for landscape first.
   * Once per SESSION, not per page: each beat hand-over is a navigation, and a
   * hint at every chapter would be nagging. Rotating to landscape removes it at
   * once; rotating back does not bring it again.
   */
  #updateRotateHint(): void {
    const hint = this.#rotateHint;
    if (!hint) return;
    if (window.innerWidth >= window.innerHeight) {
      hint.classList.remove('is-shown');
      return;
    }
    if (hint.classList.contains('is-shown') || rotateHintSeen()) return;
    hint.classList.add('is-shown');
    markRotateHintSeen();
  }

  #button(action: TouchAction, label: string, glyph: string): HTMLButtonElement {
    const element = document.createElement('button');
    element.type = 'button';
    element.className = 'rosalie-touch-action';
    if (action === 'pause') element.classList.add('rosalie-touch-pause');
    else element.style.gridArea = action;
    element.dataset.action = action;
    element.setAttribute('aria-label', label);
    element.textContent = glyph;
    const button: TouchButton = { action, element, pointerId: null };
    this.#buttons.push(button);

    const onDown = (event: PointerEvent): void => {
      if (event.pointerType !== 'touch') return;
      event.preventDefault();
      this.#markUsed();
      this.#capture(element, event.pointerId);
      // An unoffered slot swallows the touch and does nothing. Pressing an
      // invisible WATCH with nothing in reach would otherwise raise her map
      // (`observe` with no target still does, D-237) from a button that is not
      // there.
      if (!this.#offered(action) || button.pointerId !== null) return;
      // Ownership latches here and holds until THIS pointer lifts, whatever
      // availability does meanwhile: ROOT and WATCH are holds, and a verb that
      // stopped being offered mid-hold must still see its release.
      button.pointerId = event.pointerId;
      element.classList.add('is-active');
      this.input.setTouchAction(action, true);
    };
    const onEnd = (event: PointerEvent): void => {
      if (event.pointerType !== 'touch') return;
      event.preventDefault();
      this.#releaseCapture(element, event.pointerId);
      if (button.pointerId !== event.pointerId) return;
      button.pointerId = null;
      element.classList.remove('is-active');
      this.input.setTouchAction(action, false);
      // The hold was what kept an unoffered slot shown, and availability only
      // re-applies when it changes — which it did mid-hold, not now. Without
      // this the slot stayed lit and dead under the thumb after the release.
      this.#applyVerbAvailability();
    };
    element.addEventListener('pointerdown', onDown);
    element.addEventListener('pointerup', onEnd);
    element.addEventListener('pointercancel', onEnd);
    return element;
  }

  /** Whether a press on this slot does anything right now. */
  #offered(action: TouchAction): boolean {
    const { interact, observe, call, sheet } = this.#verbAvailability;
    // While a sheet is up, the verbs step out of its way — except the control
    // holding it, whose release is what lowers it.
    if (sheet !== null && (action === 'interact' || action === 'observe' || action === 'notes' || action === 'callHolly')) {
      return action === sheet;
    }
    switch (action) {
      case 'interact': return interact !== null;
      case 'observe': return observe !== null;
      case 'callHolly': return call !== null;
      default: return true;
    }
  }

  #applyVerbAvailability(): void {
    const { interact, call } = this.#verbAvailability;
    for (const button of this.#buttons) {
      const { action, element } = button;
      const offered = this.#offered(action);
      // Held buttons keep their look until released (see `onDown`).
      const shown = offered || button.pointerId !== null;
      element.classList.toggle('is-unoffered', !shown);
      // Gait and pause are always there and stay at the resting opacity; only
      // the verbs light up when offered.
      const lit = shown && action !== 'sneak' && action !== 'roll' && action !== 'pause';
      element.classList.toggle('is-available', lit);
    }
    this.#setWord('interact', interact ?? 'nudge');
    this.#setWord('callHolly', call ?? 'call');
  }

  #setWord(action: 'interact' | 'callHolly', verb: string): void {
    const element = this.#buttons.find((button) => button.action === action)?.element;
    const word = VERB_WORDS[verb];
    if (element === undefined || word === undefined) return;
    if (element.textContent !== word.glyph) element.textContent = word.glyph;
    if (element.getAttribute('aria-label') !== word.label) element.setAttribute('aria-label', word.label);
  }

  #applyLayout(): void {
    const root = this.#root;
    if (!root) return;
    const { button, gap, margin, stickRadius } = this.#layout;
    root.style.setProperty('--button', `${String(button)}px`);
    root.style.setProperty('--gap', `${String(gap)}px`);
    root.style.setProperty('--margin', `${String(margin)}px`);
    root.style.setProperty('--stick-r', `${String(stickRadius)}px`);
  }

  /**
   * Re-derive the sizes, and drop every touch if the device ROTATED.
   *
   * Only on a rotation. A phone browser also resizes when its toolbar slides
   * away as the page is used, and releasing the stick then would stop her
   * mid-walk for no reason the player can see. A rotation is different: the
   * stick's origin and every look finger's last position are in the old
   * coordinate space, and a held verb whose button has just moved under the
   * thumb would stay held.
   */
  #resize(): void {
    this.#layout = touchLayout(window.innerWidth, window.innerHeight);
    this.#applyLayout();
    this.#updateRotateHint();
    const landscape = window.innerWidth > window.innerHeight;
    if (landscape === this.#landscape) return;
    this.#landscape = landscape;
    this.#clearTouches();
  }

  #beginSurfaceTouch(event: PointerEvent): void {
    if (!this.#target || this.#touches.has(event.pointerId)) return;
    event.preventDefault();
    // Touch is input, so it may timestamp its short synthetic-click suppression
    // window without feeding wall-clock time into the fixed-step simulation.
    this.#suppressClickUntil = performance.now() + 750;
    this.#markUsed();
    this.#capture(this.#target, event.pointerId);
    if (event.clientX < window.innerWidth / 2 && this.#movePointerId === null) {
      this.#movePointerId = event.pointerId;
      this.#touches.set(event.pointerId, { kind: 'move' });
      this.#placeStick(event.clientX, event.clientY, 0, 0);
      this.input.setTouchAxis(0, 0, true);
      return;
    }
    this.#touches.set(event.pointerId, { kind: 'look', lastX: event.clientX, lastY: event.clientY });
  }

  #moveSurfaceTouch(event: PointerEvent): void {
    const touch = this.#touches.get(event.pointerId);
    if (!touch || event.pointerType !== 'touch') return;
    event.preventDefault();
    if (touch.kind === 'move') {
      const stick = this.#stick;
      if (!stick) return;
      const originX = Number.parseFloat(stick.dataset.originX ?? '0');
      const originY = Number.parseFloat(stick.dataset.originY ?? '0');
      this.#updateStick(event.clientX - originX, event.clientY - originY);
      return;
    }
    const scale = touchLookScale(window.innerWidth, window.innerHeight);
    this.input.injectLook((event.clientX - touch.lastX) * scale, (event.clientY - touch.lastY) * scale);
    touch.lastX = event.clientX;
    touch.lastY = event.clientY;
  }

  #endSurfaceTouch(event: PointerEvent): void {
    const touch = this.#touches.get(event.pointerId);
    if (!touch || event.pointerType !== 'touch') return;
    event.preventDefault();
    this.#touches.delete(event.pointerId);
    this.#releaseCapture(this.#target, event.pointerId);
    if (touch.kind === 'move') {
      this.#movePointerId = null;
      this.input.setTouchAxis(0, 0, false);
      this.#stick?.classList.remove('is-active');
    }
  }

  #markUsed(): void {
    this.#root?.classList.add('is-used');
  }

  #clearTouches(): void {
    this.#touches.clear();
    this.#movePointerId = null;
    this.input.setTouchAxis(0, 0, false);
    this.#stick?.classList.remove('is-active');
    for (const button of this.#buttons) {
      button.pointerId = null;
      button.element.classList.remove('is-active');
      this.input.setTouchAction(button.action, false);
    }
    this.#applyVerbAvailability();
  }

  #placeStick(originX: number, originY: number, knobX: number, knobY: number): void {
    if (!this.#stick) return;
    this.#stick.dataset.originX = String(originX);
    this.#stick.dataset.originY = String(originY);
    this.#stick.style.left = `${String(originX)}px`;
    this.#stick.style.top = `${String(originY)}px`;
    this.#stick.style.setProperty('--knob-x', `${String(knobX)}px`);
    this.#stick.style.setProperty('--knob-y', `${String(knobY)}px`);
    this.#stick.classList.add('is-active');
  }

  #updateStick(dx: number, dy: number): void {
    const radius = this.#layout.stickRadius;
    const deadZone = TOUCH_STICK_DEAD_ZONE;
    const distance = Math.hypot(dx, dy);
    const clamped = Math.min(distance, radius);
    const directionX = distance === 0 ? 0 : dx / distance;
    const directionY = distance === 0 ? 0 : dy / distance;
    this.#placeStick(
      Number.parseFloat(this.#stick?.dataset.originX ?? '0'),
      Number.parseFloat(this.#stick?.dataset.originY ?? '0'),
      directionX * clamped,
      directionY * clamped,
    );

    if (clamped <= deadZone) {
      this.input.setTouchAxis(0, 0, true);
      return;
    }
    const magnitude = (clamped - deadZone) / (radius - deadZone);
    this.input.setTouchAxis(directionX * magnitude, -directionY * magnitude, true);
  }

  #capture(target: HTMLElement, pointerId: number): void {
    try {
      target.setPointerCapture(pointerId);
    } catch {
      // Synthetic PointerEvents cannot be captured, but production touches can;
      // retaining the event path makes the same module testable without a device.
    }
  }

  #releaseCapture(target: HTMLElement | null, pointerId: number): void {
    if (!target) return;
    try {
      if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
    } catch {
      // A cancelled pointer may already have been released by the browser.
    }
  }
}
